import React from 'react';
import { useQuery } from '@apollo/client';
import { Box, Typography } from '@material-ui/core';
import { SecondaryButton } from '../../_core/_ui/buttons.component';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';

import { GET_PUBLIC_PLAYER_HEADER } from '../../../lib/queries/attendance-response.queries';
import { HANDEDNESS_LABELS } from '../../../lib/types';
import { formatDateOnly } from '../../../lib/helpers/date.helpers';
import { formatMilestoneDisplay } from '../../../lib/helpers/player-milestone.helpers';
import { OrganizationLogo } from './organization-logo.component';
import { Fact } from './fact.component';
import { PlayerPageCard } from './player-page-card.component';

interface IPublicEditPlayerHeaderProps {
  playerAssessmentId: string;
  token: string | null;
  // Edit toggle is owned by the page so it can gate the editor panel below.
  // Optional: when omitted, the Edit affordance is suppressed entirely.
  editing?: boolean;
  onToggleEdit?: () => void;
}

// Identifies the player + organization behind the public player page. Renders
// a tight summary of all player-level data (nickname, DOB, handedness, position,
// age group, season) so the parent can see the full record at a glance, with
// an Edit toggle that reveals the editor panel below.
export const PublicEditPlayerHeader: React.FC<IPublicEditPlayerHeaderProps> = ({
  playerAssessmentId,
  token,
  editing,
  onToggleEdit,
}) => {
  const { data } = useQuery(GET_PUBLIC_PLAYER_HEADER, {
    variables: { playerAssessmentId, token },
    fetchPolicy: 'cache-and-network',
  });

  const header: IPublicPlayerHeader | undefined = data && data.publicPlayerHeader;
  if (!header) return null;

  const playerName = `${header.firstName} ${header.lastName}`.trim();
  const handednessLabel = header.handedness ? HANDEDNESS_LABELS[header.handedness as keyof typeof HANDEDNESS_LABELS] : undefined;
  const dobLabel = formatDateOnly(header.dateOfBirth) || undefined;
  // Stored metric values, formatted as "180 cm / 5'11"" + "75 kg / 165 lbs".
  const heightLabel = typeof header.heightCm === 'number' ? formatMilestoneDisplay('height', header.heightCm) : undefined;
  const weightLabel = typeof header.weightKg === 'number' ? formatMilestoneDisplay('weight', header.weightKg) : undefined;

  return (
    <PlayerPageCard padding="12px 16px" gutterBottom>
      <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <OrganizationLogo
          logoUrl={header.organizationLogoUrl}
          organizationName={header.organizationName}
        />
        <Box style={{ minWidth: 0, flex: '1 1 auto' }}>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', lineHeight: 1.2 }}
          >
            {header.organizationName}
          </Typography>
          <Typography
            variant="h6"
            style={{ fontWeight: 600, lineHeight: 1.25, marginTop: 2 }}
          >
            {playerName}
            {header.nickname && (
              <Typography
                component="span"
                variant="body2"
                color="textSecondary"
                style={{ marginLeft: 8, fontWeight: 400 }}
              >
                &ldquo;{header.nickname}&rdquo;
              </Typography>
            )}
          </Typography>
        </Box>
        {onToggleEdit && (
          <SecondaryButton
            variant="outlined"
            size="small"
            startIcon={editing ? <CloseIcon fontSize="small" /> : <EditIcon fontSize="small" />}
            onClick={onToggleEdit}
            aria-expanded={!!editing}
            aria-label={editing ? 'Close player editor' : 'Edit player details'}
          >
            {editing ? 'Close' : 'Edit'}
          </SecondaryButton>
        )}
      </Box>

      {/* Minimal display of all player data. Hidden facts collapse — Fact
          suppresses empty values so the grid stays tight. */}
      <Box
        component="dl"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 12,
          margin: '12px 0 0',
        }}
      >
        <Fact label="Date of birth" value={dobLabel} />
        <Fact label="Handedness" value={handednessLabel} />
        <Fact label="Position" value={header.positionName} />
        <Fact label="Age group" value={header.ageGroupName} />
        <Fact label="Season" value={header.assessmentName} />
        <Fact label="Height" value={heightLabel} />
        <Fact label="Weight" value={weightLabel} />
      </Box>
    </PlayerPageCard>
  );
};
